import { put, call, select, takeLatest, takeEvery } from 'redux-saga/effects';
import { setSaveInProgress } from '../actions/test';
import { showErrorToast } from '../actions/toast';
import { runCodeWithCustomInput, runCodeWithTestCases } from '../api/coding';
import { fetchRepo } from '../api/test';
import ActionTypes from '../constants/ActionTypes';
import { NETWORK_ERROR, SAVE_IN_PROGRESS } from '../constants/Http';
import { getApiErrorData } from '../utils/helperFunction';
import { errorLogger } from '../utils/trackingUtil';
import { customSortByname } from '../utils/utils';

function* setProblemData(args) {
  const { problem } = args;
  let allowedTechnologies = [];
  if (!problem) return;

  const { technologies, test } = yield select(state => ({
    technologies: state.coding.technologies.technologies,
    test: state.test,
  }));

  const { allowed_technologies } = test;
  const allowedTechnologiesForProblemFromTest = allowed_technologies
    ? allowed_technologies[problem.slug]
    : undefined;

  let techs;
  if (problem.test_environment) {
    /**
     * Check if all techs have '/api/v1/technology/'
     */
    techs = problem.technologies.map(tech =>
      tech.includes('/api/v1/technology/') ? tech : `/api/v1/technology/${tech}`
    );
  } else techs = problem.technologies;

  if (
    Array.isArray(allowedTechnologiesForProblemFromTest) &&
    allowedTechnologiesForProblemFromTest.length > 0
  )
    allowedTechnologies = technologies[problem.problem_type].filter(tech =>
      allowedTechnologiesForProblemFromTest.includes(tech.resource_uri)
    );
  else if (problem.technologies.length > 0)
    allowedTechnologies = technologies[problem.problem_type].filter(tech =>
      techs.includes(tech.resource_uri)
    );
  else allowedTechnologies = technologies[problem.problem_type];

  allowedTechnologies = allowedTechnologies.sort(customSortByname);

  yield put({
    type: `${ActionTypes.SET_DESMOND_PROBLEM_DATA}_SUCCESS`,
    payload: {
      allowedTechnologies,
    },
    problemSlug: problem.slug,
  });
}

function* onSubmitToEvaluate(args) {
  try {
    const { solutionId, problem } = args;
    const socket = yield select(state => state.socket);
    yield put({
      type: ActionTypes.RESET_RUN_DESMOND_DATA,
      problemSlug: problem.slug,
    });

    yield put({
      type: `${ActionTypes.RUN_DESMOND_TYPE_TEST_CASES}_SET_LISTINER`,
      solutionId,
      problem,
    });
    yield call(runCodeWithTestCases, {
      solution_id: solutionId,
      sockmeta: {
        connid: socket.connid,
        wid: socket.wid,
        sockid: socket.sockid,
      },
    });
  } catch (error) {
    let errorInfo = {};
    if (error?.config?.url?.includes('solutions.submit')) {
      errorInfo = getApiErrorData(error, 'SCD');
    }

    yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));

    errorLogger(error);
  }
}

function* getRepo(args) {
  try {
    const { path, solutionSlug, problemSlug, fileType } = args;
    yield put(setSaveInProgress(problemSlug, true));
    const resp = yield call(fetchRepo, path, solutionSlug, fileType);
    yield put({
      type: ActionTypes.DESMOND_PROBLEM_SAVE_REPO,
      payload: { data: resp, problemSlug },
    });
  } catch (error) {
    errorLogger(error);
  } finally {
    yield put(setSaveInProgress(args.problemSlug, false));
  }
}

function* onRunCodeWithCustomInput(args) {
  try {
    const auth = yield select(state => state.auth.user);
    const socket = yield select(state => state.socket);
    const { technologiesMapByUri } = yield select(
      state => state.coding.technologies
    );

    yield put({
      type: ActionTypes.RESET_RUN_DESMOND_DATA,
      problemSlug: args.problem.slug,
    });

    const testSolutionWithNonSampleTestcase =
      args.problem.problem_type === 'UIX' ? { non_sample_testcases: null } : {};

    yield put({
      type: `${ActionTypes.RUN_DESMOND_TYPE_TEST_CASES}_SET_LISTINER`,
      solutionId: args.solutionId,
      problem: { ...args.problem, ...testSolutionWithNonSampleTestcase },
    });

    const testCasesModeType =
      args.problem.problem_type === 'UIX' ? 'sample' : 'all';

    const resp = yield call(
      runCodeWithCustomInput,
      args.problem.problem_type,
      {
        problem_id: args.problem.id,
        sockmeta: {
          connid: socket.connid,
          wid: socket.wid,
          sockid: socket.sockid,
        },
        solution_id: args.solutionId,
        solution_slug: args.solutionSlug,
        technology: technologiesMapByUri[args.technology].slug,
        username: auth.username,
      },
      testCasesModeType
    );
    yield put({
      type: `${ActionTypes.RUN_DESMOND_CODE_CUSTOM_INPUT}_SUCCESS`,
      payload: resp.data,
      problemSlug: args.problem.slug,
    });
  } catch (error) {
    const { toast } = yield select(state => state.appStatus);
    let errorInfo;

    if (error.message !== NETWORK_ERROR && error.message !== SAVE_IN_PROGRESS)
      if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
        yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
      }
    errorLogger(error);
  }
}

function* onRunProject(args) {
  try {
    const auth = yield select(state => state.auth.user);
    const socket = yield select(state => state.socket);
    const { technologiesMapByUri } = yield select(
      state => state.coding.technologies
    );

    const resp = yield call(runCodeWithCustomInput, args.problem.problem_type, {
      filepath: args.filePath,
      problem_id: args.problem.id,
      sockmeta: {
        connid: socket.connid,
        wid: socket.wid,
        sockid: socket.sockid,
      },
      solution_id: args.solutionId,
      solution_slug: args.solutionSlug,
      technology: technologiesMapByUri[args.technology].slug,
      username: auth.username,
    });

    yield put({
      type: `${ActionTypes.RUN_PROJECT}_SUCCESS`,
      payload: resp.data,
      problemSlug: args.problem.slug,
    });
  } catch (error) {
    const { toast } = yield select(state => state.appStatus);
    let errorInfo;

    if (error.message !== NETWORK_ERROR && error.message !== SAVE_IN_PROGRESS)
      if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
        yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
      }
    errorLogger(error);
  }
}

export default function* loadDesmondSaga() {
  yield takeLatest(ActionTypes.SET_DESMOND_PROBLEM_DATA, setProblemData);

  yield takeEvery(ActionTypes.DESMOND_PROBLEM_EVALUATE, onSubmitToEvaluate);
  yield takeEvery(ActionTypes.DESMOND_PROBLEM_REPO, getRepo);
  yield takeLatest(
    ActionTypes.RUN_DESMOND_CODE_CUSTOM_INPUT,
    onRunCodeWithCustomInput
  );
  yield takeLatest(ActionTypes.RUN_PROJECT, onRunProject);
}
